import React, { useState, useRef , useEffect, useCallback  } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ReactTabulator } from 'react-tabulator';
import {Card} from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import "react-datepicker/dist/react-datepicker.css";
import "tabulator-tables/dist/css/tabulator_semanticui.min.css"; 
import { jwtDecode } from 'jwt-decode';
import loader from '../../assets/img/loading.gif'
import API from '../../api/api.jsx';
import Settings_002_01 from './settings_002_01'
import { API_BASE_URL } from '../../config/app-axios.jsx';
import { clearUserState } from '../../reducers/userReducers.jsx';
import { clearMenuState } from '../../reducers/menuReducers.jsx';
import { clearLangCodeState } from '../../reducers/langCodeReducers.jsx';

// 수수료관리 화면
const Settings002 = () => {
	const { t, i18n } = useTranslation();
	const dispatch    = useDispatch();
	const tableRef    = useRef(null);

	// 초기 값
	const [loading, setLoading]           = useState(false);
	const [rowsPerPage, setRowsPerPage]   = useState(20);
	const [detailData, setDetailData]     = useState([]);
	const [filterField, setFilterField]   = useState('fee_name');
	const [filterType, setFilterType]     = useState('like');
	const [filterValue, setFilterValue]   = useState('');
	const [totalCount, setTotalCount]     = useState(0);

	// 모달폼오픈
	const [showModal, setShowModal]       = useState(false);

	// 추가 , 전종표 2023.11.30
	const [feeInfoOptions, setFeeInfoOptions] = useState([]);

	// remote 방식에 따른 페이징 처리
	const apiUrl   = API_BASE_URL;
	const userInfo = useSelector(state => state.login.userInfo);
	const token    = userInfo?.access_token;

	// ①. 강제로 쿠키 삭제를 통한 로그인 정보가 없는 경우
    const persistInfo = sessionStorage.getItem('persist:login');

    if (!persistInfo) {
        window.location.href = '/login';
    };

	// ②. 토큰 만료 체크 루틴
	if (token) {
		const decodedToken            = jwtDecode(token);
		const expirationTimeInSeconds = decodedToken.exp;
		const currentTimeInSeconds    = Math.floor(Date.now() / 1000);

		if (expirationTimeInSeconds < currentTimeInSeconds) {
			// 토큰이 만료되었을 경우 로그인 페이지로 리디렉션
			dispatch(clearUserState());
			dispatch(clearMenuState());
			dispatch(clearLangCodeState());
			localStorage.removeItem('langCodeInfo');
			window.location.href = '/login';
		};
	};

	// i18 선택된 언어에 따라 필요한 필드 값 번역
	const translateItem = useCallback((item, language) => ({
		...item,
		fee_type : item.fee_type ? t(`FEE_TYPE.${item.fee_type}`, { lng: language }) : '',
	}), [t]);

	useEffect(() => {
		const fetchBaseOptions = async () => {
			try {
				const data = await API.fetchBaseOptions();

				const options       = data.filter(item => item.code_nick_name === '수수료정보');
				const branchOptions = options.map(item => [item.code,item.code_se]);
				
				setFeeInfoOptions(branchOptions);

			} catch (error) {
				console.error('Error fetching data:', error);
			}
		};

		fetchBaseOptions();
	}, []); // 빈 배열은 컴포넌트가 마운트될 때 한 번만 실행

	// Tabulator columns 목록
	const titleColumns = (t) => {
		return [
			{formatter:"rowSelection", titleFormatter:"rowSelection", headerHozAlign: 'center', hozAlign:"center", headerSort:false, width:30, cellClick:function(e, cell){
				cell.getRow().toggleSelect();
			}},
			{ title: `<i class="fa-solid fa-link"></i> ${t('PREFERENCES.수수료명', { defaultValue: '수수료명' })}`, field: 'fee_name', headerHozAlign: 'center', hozAlign: 'center', width:300, 
				formatter: function (cell, formatterParams, onRendered) {
					cell.getElement().style.fontWeight = 'bold';
					cell.getElement().style.color = 'green';
					return cell.getValue();
				},
				cellClick:function(e, cell){
					const rowData = cell.getRow().getData();
					handleCellClick(rowData);
				},
			},
			{ title: t('PREFERENCES.수수료', { defaultValue: '수수료' }), field: 'fee', headerHozAlign: 'center', hozAlign: 'right', width:150, formatter:"money", formatterParams:{ decimal:",", thousand:".", negativeSign:true, precision:false },  },
			{ title: t('PREFERENCES.수수료정보', { defaultValue: '수수료정보' }), field: 'fee_type', headerHozAlign: 'center', hozAlign: 'center', width:300 },
			{ title: t('PREFERENCES.회수처리순번', { defaultValue: '회수처리순번' }), field: 'sort_sn', headerHozAlign: 'center', hozAlign: 'center', width:150 },
		];
	};

	const [columns, setColumns] = useState(titleColumns(t));

	// Tabulator Options
	// options 객체를 상태로 관리
	const [options, setOptions] = useState({
		ajaxURL     : `${apiUrl}/settings/settings_002`,
		ajaxConfig  : {
			method  : 'GET',
			headers : {
				'Content-Type' : 'application/json',
				'Authorization': `Bearer ${token}`,
			},
		},
		ajaxParams : {
			page : tableRef.current && tableRef.current.table ? tableRef.current.getPage() : 1,
			size : tableRef.current && tableRef.current.table ? tableRef.current.getPageSize() : 20, // 페이지 크기 정보
		},
		layout                 : "fitColumns",
		pagination             : true,
		paginationMode         : "remote",
		paginationSize         : rowsPerPage,
		paginationSizeSelector : [20, 40, 60, 80, 100],
		ajaxResponse : function (url, params, response) {

			const total_count    = response.total_count || 0;
			const translatedData = response.data.map(item => translateItem(item, i18n.language));
			const responseData   = {
				last_page : response.last_page,
				data      : translatedData,
			};

			setTotalCount(total_count);

			return responseData;
		}
	});

	// 로딩 중일 때 이전의 pagination 관련 정보를 유지하도록 설정
	const loadingOptions = {
		pagination             : true,
		paginationMode         : "remote",
		paginationSize         : rowsPerPage, // 이전 페이지 사이즈를 유지
		paginationSizeSelector : [20, 40, 60, 80, 100],
	};

	// 조회 버튼 작동 시 실행되는 함수
	const handleQuery = useCallback(
		async () => {
			setLoading(true);

			// ReactTabulator의 내부 로직을 사용하여 API 호출
			try {
				// ReactTabulator에서 사용하는 API URL과 Params를 사용하여 호출
				const url  = new URL(options.ajaxURL);
				url.search = new URLSearchParams({
					...options.ajaxParams,
					page         : tableRef.current?.getPage(),     // 현재 페이지 정보
					size         : tableRef.current?.getPageSize(), // 페이지 크기 정보
				});

				const data = await fetch(url, {
					method  : options.ajaxConfig.method,
					headers : options.ajaxConfig.headers,
				});

				const responseData = await data.json();

				// 전달 받은 총 데이터 건수
				const total_count = responseData.total_count || 0;
		
				// 번역이 필요한 부분
				const translatedData = responseData.data.map(item => translateItem(item, i18n.language));
		
				// 페이징 및 데이터 리턴
				const updatedData = {
					last_page : responseData.last_page, // 전체 페이지 수 계산
					data      : translatedData,         // 실제 테이블 데이터
				};

				// 총 건수를 화면에 표현하기 위해서
				setTotalCount(total_count);

				// 데이터 변경
				if (tableRef.current) {
					tableRef.current.setData(updatedData.data || []);
				}

				setLoading(false);

			} catch (error) {
				console.error('Error in fetchSettings002Data : ', error);
				setLoading(false);
			} 
		}, [options, i18n.language, translateItem]
	);

	// Cell 클릭 이벤트(수정)
	const handleCellClick = async (rowData) => {
		try {
			// ReactTabulator에서 사용하는 API URL과 Params를 사용하여 호출
			const url  = new URL(options.ajaxURL);
			url.search = new URLSearchParams({
				...options.ajaxParams,
				fee_id : rowData.fee_id,
				page   : tableRef.current?.getPage(),     // 현재 페이지 정보
				size   : tableRef.current?.getPageSize(), // 페이지 크기 정보
			});

			const data = await fetch(url, {
				method  : options.ajaxConfig.method,
				headers : options.ajaxConfig.headers,
			});

			const responseData = await data.json();

			setDetailData(responseData.data)

			setLoading(false);

		} catch (error) {
			console.error('Error in fetchSettings002FeeDetail : ', error);
			setLoading(false);
		} 

		setShowModal(true);
	}

	//수수료 추가 버튼
	const handleButtonClick = () => {		
		setDetailData([]);
		setShowModal(true);
	};

	// 모달창 닫기
	const handleClose = () => {
		setDetailData([]);
		setShowModal(false);
		handleQuery();
	};

	useEffect(() => {
		setColumns(titleColumns(t));
	}, [i18n.language, t]);

	// Filter 값에 따른 Tabulator 변화가 일어나는 함수
	const updateFilter = () => {
		if (tableRef.current) {
			tableRef.current.setFilter(filterField, filterType, filterValue);
		}
	};

	// value값을 입력할 때마다 해당되는 Tabulator가 화면에 보이도록 함수호출하는 부분
	const handleKeyUp = () => {
		updateFilter();
	};

	// UI
	return (
		<Card style={{ height: '100%' }}>
			<Card.Header><strong>{t('MENU_I18N.수수료 관리', { defaultValue: '수수료 관리' })}</strong></Card.Header>
			<Card.Body className='responsive-card-body'>
				<div className="panel-body">
					<div className="row ms-0">
						<div className="form-group col-lg-11 col-md-12 col-xs-12 ps-0 rounded-2"></div>
						<div className="form-group col-lg-1 col-md-12 col-xs-12 ps-0 rounded-2">
							<div className="mb-3"></div>
							<button type="button" className="btn btn-success float-end float-bottom w-100 h-30.5px align-baseline mt-8px" onClick={handleQuery}>{t('LOAN_APPLICATION.조회', { defaultValue: '조회' })}</button>
						</div>

						<div className="mt-3 mb-3 ps-0">
							<i id="filter-icon" className="cursor-pointer fa fa-plus-square" data-bs-toggle="collapse" href="#collapseExample" aria-expanded="false" aria-controls="collapseExample"></i>
							<span> Filter</span>
						</div>

						<div className="collapse show" id="collapseExample">
							<div className="row">
								<div className="form-group col-lg-2 col-md-12 col-xs-12 ps-0 rounded-2 mb-1">
									<select 
										id="filter-field" 
										className="form-select col-sm-3" 
										value={filterField}
										onChange={(e) => setFilterField(e.target.value)}
									>
										<option value="name">{t('PREFERENCES.수수료명', { defaultValue: '수수료명' })}</option>
									</select>
								</div>
					
								<div className="form-group col-lg-2 col-md-12 col-xs-12 ps-0 rounded-2 mb-1">
									<select 
										id="filter-type" 
										className="form-select col-sm-3"
										value={filterType}
										onChange={(e) => setFilterType(e.target.value)}
									>
										<option value="like">LIKE</option>
										<option value="=">=</option>
										<option value="<">&lt;</option>
										<option value="<=">&le;</option>
										<option value=">">&gt;</option>
										<option value=">=">&ge;</option>
										<option value="!=">!=</option>
									</select>
								</div>
					
								<div className="form-group col-lg-8 col-md-12 col-xs-12 ps-0 mb-3 rounded-2">
									<input 
										id="filter-value" 
										type="text" 
										className="form-control col-sm-6" 
										name="filter-value" 
										placeholder={t('COMMON.검색할 내용을 입력하세요', { defaultValue: '검색할 내용을 입력하세요' })}
										value={filterValue}
										onChange={(e) => setFilterValue(e.target.value)}
										onKeyUp={handleKeyUp}
									/>
								</div>
							</div>
						</div>
					</div>
				</div>

				{/* 로딩 관련 gif */}
				{loading && 
					<div
						style={{
							position  : 'fixed',
							top       : '50%',
							left      : '55%',
							transform : 'translate(-50%, -50%)',
							zIndex    : 9999,
						}}
					>
						<img src={loader} alt="Loading..." />
					</div>
				}

				{/* Tabulator */}
				<div id="tabulator-total-info" className='d-flex justify-content-end fw-bold'>{t(`LOAN_APPLICATION.총 {{count}}건`, { count: totalCount, defaultValue: '총 {{count}}건' })}</div>
				<ReactTabulator
					key         = {i18n.language + JSON.stringify(columns) + JSON.stringify(options)} // 언어 변경 및 옵션 변경에 따라 컴포넌트 리렌더링
					onRef       = {(r) => (tableRef.current = r.current)}
					columns     = {columns}
					options     = {loading ? loadingOptions : options} // 로딩 중일 때는 이전의 pagination 관련 정보를 유지하고 데이터를 변경하지 않도록 함
					placeholder = "No Data Found"
					className   = "responsive-tabulator"
				/>
			</Card.Body>

			<div className="card-footer d-flex justify-content-start mt-auto">
				<button type="button" className="btn btn-outline-primary mb-1 me-1 w-auto"  onClick={handleButtonClick}>{t('FEE_MANAGEMENT.수수료 추가', { defaultValue: '수수료 추가' })}</button>
			</div>
			
			<Settings_002_01 
				showModal={showModal} 
				handleClose={handleClose} 
				feeInfoOptions={feeInfoOptions} 
				DetailData={detailData.length > 0 ? detailData[0] : null}
			/>
		</ Card>
	);
};

export default Settings002;